import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { PowerFunnelEvents, platform } from '../Analytics/constants';
import { trackEvent } from '../Analytics/TrackedClick';
import { UPSELL_CONTENT } from './constants';

import styles from './annualPowerUpsell.scss';

const BenefitCard = ({ section }) => (
    <>
        <div className={styles.benefitCard}>
            <div className={styles.benefitCardHeader}>
                {section.logo ? (
                    <img className={styles.benefitCardLogo} src={section.logo} alt={section.title} />
                ) : (
                    <>
                        {section.icon && <img className={styles.benefitCardIcon} src={section.icon} alt={section.title} />}
                        <span className={styles.benefitCardTitle}>{section.title}</span>
                    </>
                )}
            </div>
            {section.benefits ? (
                <ul className={styles.benefitCardList}>
                    {section.benefits.map((benefit) => (
                        <li key={benefit}>{benefit}</li>
                    ))}
                </ul>
            ) : (
                <p className={styles.benefitCardDescription}>{section.description}</p>
            )}
        </div>
        {section.footnote && (
            <p className={styles.benefitCardFootnote}>{section.footnote}</p>
        )}
    </>
);

BenefitCard.propTypes = {
    section: PropTypes.shape({
        title: PropTypes.string.isRequired,
        logo: PropTypes.string,
        icon: PropTypes.string,
        benefits: PropTypes.arrayOf(PropTypes.string),
        description: PropTypes.string,
        footnote: PropTypes.string,
    }).isRequired,
};

const AppleSubscriptionMessage = () => (
    <div className={styles.appleMessage} data-test="apple-subscription-message">
        <p className={styles.appleMessageTitle}>{UPSELL_CONTENT.appleMessage.title}</p>
        <p className={styles.appleMessageBody}>
            {UPSELL_CONTENT.appleMessage.body}
            <span className={styles.appleMessageBodyEmphasis}>
                {UPSELL_CONTENT.appleMessage.bodyEmphasis}
            </span>
        </p>
    </div>
);

const AnnualPowerUpsell = ({
    isPTTEvent,
    isMonthlySubscriber,
    isAppleMonthlySubscriber,
    onGetAnnualPlan,
    ctaTextOverride,
    redirectNoteOverride,
    sourceEventId,
    userSubscriptionStatus,
    referringSurface,
    gatedFeature,
}) => {
    const content = isPTTEvent ? UPSELL_CONTENT.ptt : UPSELL_CONTENT.nonPtt;

    const regularSections = content.sections.filter((s) => !s.comingSoon);
    const comingSoonSections = content.sections.filter((s) => s.comingSoon);
    const ctaText = ctaTextOverride || (isMonthlySubscriber ? UPSELL_CONTENT.monthlyCta : UPSELL_CONTENT.freeCta);

    // Fire-once-on-mount paywall-viewed event. Empty deps are intentional so the
    // event isn't re-emitted on prop changes during the same mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!referringSurface) {
            return;
        }
        trackEvent(PowerFunnelEvents.POWER_PAYWALL_VIEWED, {
            platform,
            userSubscriptionStatus,
            referringSurface,
            gatedFeature,
            ...(sourceEventId && { sourceEventId }),
        });
    }, []);

    return (
        <div className={`${styles.annualPowerUpsell} ${isAppleMonthlySubscriber ? styles.appleSubscriber : ''}`}>
            <div className={styles.scrollableContent}>
                {isAppleMonthlySubscriber && <AppleSubscriptionMessage />}

                <p className={styles.subtitle}>{content.subtitle}</p>

                {regularSections.map((section) => (
                    <BenefitCard key={section.title} section={section} />
                ))}

                {comingSoonSections.length > 0 && (
                    <>
                        <div className={styles.comingSoonLabel}>Coming Soon</div>
                        {comingSoonSections.map((section) => (
                            <BenefitCard key={section.title} section={section} />
                        ))}
                    </>
                )}
            </div>

            <div className={styles.footer}>
                <button
                    className={`btn btn-primary btn-block ${styles.ctaButton} ${isAppleMonthlySubscriber ? styles.disabledCta : ''}`}
                    type="button"
                    onClick={onGetAnnualPlan}
                    disabled={isAppleMonthlySubscriber}
                >
                    {ctaText}
                </button>
                <p className={styles.redirectNote}>{redirectNoteOverride || UPSELL_CONTENT.redirectNote}</p>
            </div>
        </div>
    );
};

AnnualPowerUpsell.propTypes = {
    isPTTEvent: PropTypes.bool.isRequired,
    isMonthlySubscriber: PropTypes.bool,
    isAppleMonthlySubscriber: PropTypes.bool,
    onGetAnnualPlan: PropTypes.func,
    ctaTextOverride: PropTypes.string,
    redirectNoteOverride: PropTypes.string,
    sourceEventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    userSubscriptionStatus: PropTypes.string,
    referringSurface: PropTypes.string,
    gatedFeature: PropTypes.string,
};

AnnualPowerUpsell.defaultProps = {
    isMonthlySubscriber: false,
    isAppleMonthlySubscriber: false,
    onGetAnnualPlan: () => {},
    ctaTextOverride: null,
    redirectNoteOverride: null,
    sourceEventId: null,
    userSubscriptionStatus: null,
    referringSurface: null,
    gatedFeature: null,
};

export default AnnualPowerUpsell;
